/**
 * The one canonical public origin for Menuraq.
 *
 * `menuraq.com` redirects to `www.menuraq.com` at the domain level, so www is
 * the host that actually serves. Every canonical URL, sitemap entry, robots
 * `Host`, Open Graph URL and JSON-LD `@id` has to agree with that or crawlers
 * get told the canonical page lives at a URL that immediately redirects.
 *
 * Kept dependency-free so the root layout can import it without pulling server
 * modules into the document shell. If the canonical host ever changes, this is
 * the only line that should need editing.
 */
export const SITE_URL = 'https://www.menuraq.com';

/**
 * The apex, derived from SITE_URL rather than written out again. It redirects to
 * www at the domain level, but browsers still send it as a request Origin, so
 * origin allowlists and CSP `connect-src` have to name it.
 */
export const APEX_URL = SITE_URL.replace('://www.', '://');

/**
 * Chow's canonical consumer host (doc 28 R28.1). The consumer product is Chow
 * and it is served from here once the host migration is active.
 */
export const CHOW_URL = 'https://chow.menuraq.com';

/**
 * The pre-migration customer host. Pinned forever: every link Menuraq has ever
 * emitted on `c.menuraq.com` must keep redirecting (doc 28 R28.3), so this is
 * a distinct constant from the *active* customer host and never flips.
 */
export const LEGACY_CUSTOMER_URL = 'https://c.menuraq.com';

/**
 * Platform administration, on its own subdomain (doc 36).
 *
 * This was previously served from `app/admin/` inside the customer deployment,
 * reachable at a guessable `/admin` path on the public hosts. That contradicted
 * the decision recorded in `lib/routing/hosts.ts` — that the console belongs on
 * its own hostname so that listing it in the host map would neither widen the CSP
 * origin allowlist nor publish where it lives.
 *
 * The console now mounts on its own host, so the ownership rule does the work:
 * `muhid.` declares `owns: ['admin']`, and every other host 308s `/admin` here
 * rather than rendering the console inside the customer app. The route under
 * `app/admin/` still exists — one Next.js app serves every host — but it is only
 * reachable at this address.
 *
 * `noindex` in the host map, because an operator console has no business in a
 * results page.
 */
export const LEGACY_ADMIN_URL = 'https://muhid.menuraq.com';

/**
 * Read an `http(s)` origin from a public env var, or fall back.
 *
 * `NEXT_PUBLIC_*` is INLINED AT BUILD TIME, so the ops deployment bakes its own
 * hostname and the default build keeps today's. That is on purpose: the host map
 * is a static table and a hostname that could change between a render and a
 * redirect would make `platformHostForHostname` answer differently for the same
 * request.
 *
 * A malformed value FALLS BACK rather than throwing. Throwing here would be
 * loud, but it would take down every page in the app — this module is imported
 * by the root layout — over a mistyped hostname, and the fallback is the real
 * console, which is the safe direction. The failure mode this replaces (a
 * switch whose wrong position silently serves the wrong thing, doc 32 D7) does
 * not apply: there is no privilege attached to *which* of our hosts answers.
 */
function publicOrigin(raw: string | undefined, fallback: string): string {
  const value = raw?.trim().replace(/\/+$/, '');
  if (!value) return fallback;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return fallback;
    return parsed.origin;
  } catch {
    return fallback;
  }
}

/**
 * The ops console's origin — `ADMIN_URL`, and the host this repo serves
 * `app/admin/**` from. Environment-driven so the console can move to an
 * unadvertised deployment without a code change (blueprint doc 12 §9.1, OD-1).
 * Unset, it is exactly the value it has always been.
 */
export const ADMIN_URL = publicOrigin(
  process.env.NEXT_PUBLIC_ADMIN_ORIGIN,
  LEGACY_ADMIN_URL,
);

/**
 * The support panel's origin.
 *
 * Defaults to `ADMIN_URL`, because OD-1 ships ONE ops deployment with two hidden
 * mounts (`/admin`, `/support`) and Vercel gives one `.vercel.app` hostname per
 * project. Setting `NEXT_PUBLIC_SUPPORT_ORIGIN` to a second deployment's hostname
 * is what promotes support to its own project later — a config change, because
 * every consumer of this constant already treats it as "where staff work".
 */
export const SUPPORT_URL = publicOrigin(
  process.env.NEXT_PUBLIC_SUPPORT_ORIGIN,
  ADMIN_URL,
);

/**
 * The migration is DONE. Chow is the one consumer product and chow.menuraq.com
 * is the one consumer host.
 *
 * This used to be `process.env.NEXT_PUBLIC_CHOW_MIGRATION === 'true'` — a build-
 * time switch that flipped the customer host, the canonical space, the sitemap
 * and robots branches and the proxy rewrite together. Leaving it as an env flag
 * meant the whole consumer surface silently ran on the retired host whenever the
 * variable was unset, which is exactly what happened in production: Chow's own
 * domain served the marketing site and /discover 404'd.
 *
 * It is kept as an exported constant (rather than deleted outright) so the
 * places that legitimately branch on "is this the Chow era" read as intent
 * instead of a hardcoded `true`, and so the legacy redirects below can never be
 * mistaken for dead code. It does not vary.
 */
export const CHOW_MIGRATION_ACTIVE = true;

/**
 * The ACTIVE customer-facing host. Always Chow.
 *
 * `LEGACY_CUSTOMER_URL` stays a separate constant because every link Menuraq has
 * ever emitted on c.menuraq.com must keep redirecting forever (doc 28 R28.3) —
 * retiring the host is not the same as forgetting it.
 */
export const CUSTOMER_URL = CHOW_URL;

/**
 * Canonical URL for a Chow route, always on the Chow host.
 *
 * `/chow/*` is only the in-repo mount path; it is never a canonical URL, and the
 * proxy 308s it to the Chow host so no page is reachable at two addresses.
 */
export function chowCanonicalUrl(chowPath = '/'): string {
  const path = chowPath.startsWith('/') ? chowPath : `/${chowPath}`;
  return new URL(path, CHOW_URL).toString();
}

/**
 * @deprecated Use `PLATFORM_ORIGINS` from lib/routing/hosts.ts.
 *
 * This was a hand-written list of subdomains left over from the previous host
 * split, described in its own comment as "harmless-but-stale". It is not harmless:
 * it feeds billing `success_url`/`cancel_url` validation, so the moment those hosts
 * serve again it becomes live security surface — an allowlist nobody reviewed
 * deciding where a payment flow may send a customer.
 *
 * Kept only as `[APEX_URL]`, which genuinely still redirects and is genuinely still
 * a possible request Origin. Every other entry now comes from the host map, where
 * adding a host and trusting a host are the same edit (doc 32 §6.4).
 *
 * The dependency runs one way — hosts.ts imports THIS file for the registrable
 * domain — so the derived list cannot live here.
 */
export const LEGACY_REDIRECT_ORIGINS = [APEX_URL] as const;

/** Build an absolute URL on a given host from a path. */
export function canonicalUrl(path = '/', host: string = SITE_URL): string {
  return new URL(path.startsWith('/') ? path : `/${path}`, host).toString();
}

/** The shared Open Graph image URL, on the main host. */
export const OG_IMAGE_URL = canonicalUrl('/opengraph-image.png');
